import { useCallback, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { compareWithAppVersion, ZpayRelatedItem } from '@croquiscom/web2app';
import { useAtomValue } from 'jotai';
import flatten from 'lodash/flatten';
import uniq from 'lodash/uniq';
import { useRouter } from 'next/router';

import { BundlePurchaseItemList, getRecommendProductDrLogByCatalogProductId } from '@common/checkout-utils';
import { isMobile } from '@common/device-manager';
import {
  getShopProductNo,
  useUBLContext,
  ZigzagEvent,
  ZigzagEvent as ZigzagLogEvent,
  ZpayEvent,
} from '@common/log-manager';
import { Toast } from '@common/overlay-manager';
// eslint-disable-next-line @nx/enforce-module-boundaries
import {
  addZzSessionData,
  getZzSessionData,
  isPreview,
  PdpCatalogFulfillmentType,
  PdpCatalogProductImageType,
  TabType,
  useCatalogProductIdParams,
  usePdpConfig,
  usePdpRecommendSize,
  usePdpWebEvents,
  useSavedMyGoods,
  useZonlyEstimateShippingInfo,
  useZpayOptionLog,
} from '@domains/pdp-common';

import { ADD_TO_CART_COUNT_SESSION_DATA_KEY } from '../constant';
import { OrderPromotionType } from '../graphql/select-option_types.2';
import { useOptionsControl } from '../hooks/useOptionsControl';
import { cartCountQueryOption } from '../queries/cartCountQueryOption';
import { useOptions } from '../queries/useOptions';
import { BogoRequiredQuantityAtom, ItemsAtom } from '../store/baseAtoms';
import { ItemCardsAtom } from '../store/computedAtoms';
import { OptionType } from '../types/option-type';
import { addCart } from '../utils/add_cart_utils';
import {
  CreateOrderSheetArgs,
  createOrderSheetInPDP,
  createOrderSheetInPDPForKakaoPurchase,
} from '../utils/order_sheet_utils';
import {
  distributePrice,
  getAdditionalOptionDetailList,
  getBogoOriginPrice,
  getDiscountedPrice,
  getTotalInfo,
} from '../utils/utils';

interface ConfirmItemType {
  grouping_required_quantity?: number | undefined;
  id: string;
  count: number;
  price: number;
  additional_option_detail_list: ReturnType<typeof getAdditionalOptionDetailList>;
  is_additional: boolean;
  is_bogo: boolean;
  is_zigzin?: boolean;
  option_type: string | null | undefined;
}

interface Props {
  isLoggedIn: boolean;
  isCouponUnused?: boolean;
  onClose?: () => void;
  onAddCart?: (bundle_purchase_item_list: BundlePurchaseItemList) => void;
}

export const useConfirmService = ({ isCouponUnused, isLoggedIn, onClose, onAddCart }: Props) => {
  useSavedMyGoods();
  const ublContext = useUBLContext();
  const country = 'KR';
  const isKorea = country === 'KR';

  const router = useRouter();
  const _isPreview = isPreview(router.asPath);

  const { checkoutClickPurchaseKakaoUBL, addToCartUBL, checkoutClickUBL } = useZpayOptionLog();
  const { addToCartEvent } = usePdpWebEvents();

  const [couponTooltip, setCouponTooltip] = useState(false);
  const {
    funnelType: configFunnelType,
    isZigzagApp,
    isProductPreview,
    source,
    from: configFrom,
    isGroupOrderBridgeTest,
  } = usePdpConfig();
  const queryFunnelType = Array.isArray(router.query.funnel_type)
    ? router.query.funnel_type[0]
    : router.query.funnel_type;

  const queryFrom = Array.isArray(router.query.from) ? router.query.from[0] : router.query.from;
  const funnelType = configFunnelType ?? queryFunnelType;
  const from =
    configFrom ?? (queryFrom as 'recommendations' | 'cart' | 'ordersheet' | 'similar_products' | 'tf_invite_challenge');

  const { isNoOption, addItem, resetAll, bogoInfo, itemList } = useOptionsControl();

  const { refetch: refetchCartCount } = useQuery({ ...cartCountQueryOption(), enabled: false });

  const params = useCatalogProductIdParams();
  const {
    options: {
      catalog_product,
      catalog_product: {
        promotion_info,
        matching_catalog_product_info,
        store_deal_banner,
        discount_info,
        fulfillment_type,
        product_price: {
          first_order_discount,
          display_final_price: {
            final_price: { badge: displayFinalPriceBadge },
            final_price_additional: displayFinalPriceAdditional,
          },
          product_promotion_discount_info,
          max_price_info: { price: maxPrice },
          final_discount_info: { discount_price: finalDiscountPrice },
        },
        shipping_fee,
      },
      flags: { is_purchase_only_one_at_time, is_cart_button_visible },
      key_color: { discount_info_of_atf },
    },
  } = useOptions();
  const catalog_product_id = params.catalog_product_id ?? catalog_product.id;
  const promotionDiscountAmount = product_promotion_discount_info?.discount_amount ?? 0;
  const coupon_discount_info_list = catalog_product.product_price.coupon_discount_info_list ?? [];

  const { has_recommend_size } = usePdpRecommendSize();
  const itemCards = useAtomValue(ItemCardsAtom);
  const items = useAtomValue(ItemsAtom);
  const itemValues = Object.values(items);

  const bogo_required_quantity = useAtomValue(BogoRequiredQuantityAtom);

  const totalInfo = getTotalInfo(items);
  const selected_zigzin_type_list = uniq(itemCards.map((o) => o.is_zigzin));

  const isSample = Boolean(catalog_product.trait_list?.some(({ type }) => type === 'SAMPLE'));

  const matchingZigzinProductId = matching_catalog_product_info?.id ?? '';
  const matchingProductMaxPrice = matching_catalog_product_info?.product_price?.max_price_info.price ?? 0;

  const is_on_site = fulfillment_type === PdpCatalogFulfillmentType.ON_SITE;
  const isZigzinProduct = fulfillment_type === PdpCatalogFulfillmentType.ZIGZIN;
  const isZigzinMatchingProduct = isZigzinProduct && !!matching_catalog_product_info;
  const isGeneralMatchingProduct = !isZigzinProduct && !!matching_catalog_product_info;

  const isMatchingAll = selected_zigzin_type_list.length === 2;
  const hasMatchingGeneral = !!selected_zigzin_type_list.filter((o) => !o).length;
  const hasMatchingZigzin = !!selected_zigzin_type_list.filter((o) => o).length;
  const hasOnlyMatchingZigzin = hasMatchingZigzin && !hasMatchingGeneral;
  const hasOnlyMatchingGeneral = !hasMatchingZigzin && hasMatchingGeneral;
  const sendGeneralProductId = isZigzinMatchingProduct && hasMatchingGeneral && matching_catalog_product_info;
  const sendZigzinProductId = isGeneralMatchingProduct && hasMatchingZigzin && matching_catalog_product_info;

  const isMobileNaverEp = isMobile() && source?.includes('shopping_naver_all');
  const showPurchaseWithKakao = isMobileNaverEp && !isZigzagApp && !isLoggedIn;
  const finalDiscountInfo = !isCouponUnused
    ? displayFinalPriceAdditional?.badge?.text || displayFinalPriceBadge?.text || ''
    : '';
  const { pdp_zigzin_shipping_base_info } = useZonlyEstimateShippingInfo({
    catalog_product_id: isZigzinProduct ? params.catalog_product_id : matchingZigzinProductId,
    final_price: isZigzinProduct
      ? finalDiscountPrice
      : (matching_catalog_product_info?.product_price.final_discount_info.discount_price ?? 0),
    enabled: isZigzinProduct || Boolean(matching_catalog_product_info && hasMatchingZigzin),
  });
  const isDeliveryTomorrow = !pdp_zigzin_shipping_base_info?.oneday_shipping;

  const discountInfo = useMemo(() => {
    const optionDiscountInfo = itemValues
      .map((o) => o.discount_info)
      .sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0))
      .filter(Boolean)?.[0];

    const isOnlyMatchingZigzinInGeneralProduct = isGeneralMatchingProduct && hasOnlyMatchingZigzin;
    const isOnlyMatchingGeneralInZigzinProduct = isZigzinMatchingProduct && hasOnlyMatchingGeneral;
    const productDiscountInfo =
      isOnlyMatchingZigzinInGeneralProduct || isOnlyMatchingGeneralInZigzinProduct
        ? matching_catalog_product_info?.discount_info
        : discount_info;

    return optionDiscountInfo ? optionDiscountInfo : productDiscountInfo;
  }, [
    itemValues,
    isGeneralMatchingProduct,
    hasOnlyMatchingZigzin,
    isZigzinMatchingProduct,
    hasOnlyMatchingGeneral,
    matching_catalog_product_info?.discount_info,
    discount_info,
  ]);

  const confirmItems: ConfirmItemType[] = useMemo(() => {
    const finalItems = flatten(
      itemValues.map(({ count, origin_items, text_options, option_type, is_zigzin }) =>
        origin_items.map((o) => ({ ...o, count, text_options, option_type, is_zigzin })),
      ),
    );

    return finalItems.map(({ count, id, price, text_options, option_type, is_zigzin }) => {
      const additional_option_detail_list = getAdditionalOptionDetailList(text_options);
      const is_bogo = typeof bogo_required_quantity === 'number';
      return {
        id,
        count,
        price,
        additional_option_detail_list,
        is_additional: option_type === OptionType.ADDITIONAL,
        is_bogo,
        is_zigzin: Boolean(is_zigzin),
        option_type,
        ...(is_bogo && {
          grouping_required_quantity: bogo_required_quantity + 1,
        }),
      };
    });
  }, [bogo_required_quantity, itemValues]);

  const originPrice = useMemo(() => {
    return confirmItems.reduce((total, { count, option_type, price, is_zigzin }) => {
      let refined_price = getBogoOriginPrice(maxPrice, bogoInfo) * count;

      if (option_type === OptionType.ADDITIONAL) {
        refined_price = price * count;
      }

      if (isZigzinMatchingProduct) {
        const zigzinOriginPrice = is_zigzin ? maxPrice : getBogoOriginPrice(matchingProductMaxPrice, bogoInfo);

        refined_price = zigzinOriginPrice * count;
      }
      if (isGeneralMatchingProduct) {
        const zigzinOriginPrice = is_zigzin ? getBogoOriginPrice(matchingProductMaxPrice, bogoInfo) : maxPrice;

        refined_price = zigzinOriginPrice * count;
      }

      total += refined_price;

      return total;
    }, 0);
  }, [confirmItems, maxPrice, bogoInfo, isZigzinMatchingProduct, isGeneralMatchingProduct, matchingProductMaxPrice]);

  /*
    결제 예상 금액의 경우 price - coupon - first 으로 계산하여 노출하는데
    스토어딜의 경우 할인 정보를 따로 내려주지 않기 때문 final_price 값을 사용하여 노출하도록 함
  */
  const { finalPrice, zigzinProductFinalPrice } = coupon_discount_info_list.reduce(
    (previousValue, currentValue) => {
      previousValue.finalPrice = getDiscountedPrice({
        priceList: totalInfo.priceList,
        totalPrice: previousValue.finalPrice,
        couponDiscountInfo: currentValue,
        firstOrderDiscount: first_order_discount,
        isCouponUnused,
      });

      previousValue.zigzinProductFinalPrice = getDiscountedPrice({
        priceList: totalInfo.zigzinPriceList,
        totalPrice: previousValue.zigzinProductFinalPrice,
        couponDiscountInfo: currentValue,
        firstOrderDiscount: first_order_discount,
        isCouponUnused,
      });
      return previousValue;
    },
    {
      finalPrice: store_deal_banner ? totalInfo.finalPrice : totalInfo.price,
      zigzinProductFinalPrice: store_deal_banner ? totalInfo.finalPrice : totalInfo.zigzinPrice,
    },
  );

  const hasCouponDiscount = !!coupon_discount_info_list.length && totalInfo.price >= finalPrice;

  const displayOriginPrice =
    hasCouponDiscount || discountInfo || promotionDiscountAmount ? originPrice : totalInfo.price;

  const zpayRelatedItem = useMemo(() => {
    const shopProductNo = catalog_product.external_code
      ? getShopProductNo(catalog_product.shop_main_domain, catalog_product.external_code)
      : null;
    const mainImg = catalog_product.product_image_list.find(
      (img) => img.image_type === PdpCatalogProductImageType.MAIN,
    )!;
    const subImg = catalog_product.product_image_list.find((img) => img.image_type === PdpCatalogProductImageType.SUB)!;
    const imageUrl = mainImg ? mainImg?.url || mainImg?.origin_url : subImg?.url || subImg?.origin_url;

    return {
      scene: 'PRODUCT_DETAIL',
      productNo: shopProductNo,
      catalogProductId: catalog_product_id,
      imageUrl,
    };
  }, [
    catalog_product.external_code,
    catalog_product_id,
    catalog_product.product_image_list,
    catalog_product.shop_main_domain,
  ]);

  const refineOrderItem = (
    {
      id,
      price,
      count,
      additional_option_detail_list,
      is_additional,
      is_bogo,
      is_zigzin,
      grouping_required_quantity,
    }: ConfirmItemType,
    promotion_applied_discount_price?: number,
  ) => {
    const default_order_item = {
      product_item_id: id,
      price, // 주문서 생성 시 원화넘겨야함
      quantity: count,
      additional_option_detail_list,
      ...(funnelType && { funnel_info: { type: funnelType } }),
    };
    let params = {};

    if (is_bogo && !is_additional && grouping_required_quantity) {
      params = {
        grouping_required_quantity,
        promotion_applied_info: {
          promotion_applied_discount_price,
          promotion_id: promotion_info?.promotion_id,
          promotion_type: promotion_info?.promotion_type,
        },
      };
    }

    const isFirstOrderDiscount = !!first_order_discount?.promotion_id;
    const checkMatchingProductFirstOrder = isZigzinMatchingProduct && is_zigzin && isFirstOrderDiscount;
    const checkFirstOrder = !isZigzinMatchingProduct && isFirstOrderDiscount;

    if (checkMatchingProductFirstOrder || checkFirstOrder) {
      params = {
        promotion_applied_info: {
          promotion_applied_discount_price: first_order_discount.price,
          promotion_id: first_order_discount.promotion_id,
          promotion_type: OrderPromotionType.FIRST_ORDER,
        },
      };
    }

    return Object.assign(default_order_item, params);
  };

  const order_item_list = useMemo(() => {
    const bogoCount = (bogo_required_quantity ?? 1) + 1;
    const confirmPrice = confirmItems.reduce((total, item) => total + item.price, 0) / bogoCount;
    const bogoDistributePriceList = distributePrice(confirmPrice, confirmItems.length);
    return confirmItems.map((item, index) => refineOrderItem(item, bogoDistributePriceList[index]));
  }, [confirmItems, bogo_required_quantity, refineOrderItem]);

  const orderItems = useMemo((): CreateOrderSheetArgs => {
    return {
      order_item_list,
      from: 'product',
      catalog_product_id,
    };
  }, [
    bogoInfo?.discount_type,
    first_order_discount?.price,
    first_order_discount?.promotion_id,
    funnelType,
    isZigzinMatchingProduct,
    catalog_product_id,
    promotion_info?.promotion_id,
    promotion_info?.promotion_type,
    confirmItems,
  ]);

  const initialItem = useMemo(
    () =>
      isNoOption
        ? {
            ...itemList[0],
            count: 1,
            options: [],
            text_options: [],
            option_type: null,
            origin_items: [],
          }
        : null,
    [isNoOption, itemList],
  );

  const purchaseKaKao = useCallback(() => {
    checkoutClickPurchaseKakaoUBL(catalog_product_id);

    setTimeout(() => {
      createOrderSheetInPDPForKakaoPurchase(orderItems);
    }, 300);
  }, [checkoutClickPurchaseKakaoUBL, catalog_product_id, orderItems]);

  const close = () => {
    if (!onClose) {
      return;
    }
    onClose();
  };

  const getBottomRelatedButtonData = () => {
    if (isKorea && compareWithAppVersion('7.18.0') && has_recommend_size) {
      return {
        buttonTitle: '연관상품 더 보기',
        buttonAction: `window.zigzagHandler.onSavedMyGoods(${TabType.RECOMMEND});`,
      };
    } else if (compareWithAppVersion('7.18.0')) {
      return {
        buttonTitle: null,
        buttonAction: null,
      };
    }
    return {};
  };

  const getAvailableCouponData = () => {
    return {
      ...(compareWithAppVersion('8.6.0') && {
        shopId: catalog_product.shop_id,
        couponAvailableStatus: catalog_product.coupon_available_status,
      }),
    };
  };

  const sendAddToCartBrazeLog = () => {
    // BRAZE LOG: 동 세션에서 카트담기 액션 3회 이상의 경우에 로그 발송
    const addToCartCountData: Record<string, number> = getZzSessionData(ADD_TO_CART_COUNT_SESSION_DATA_KEY);
    addToCartCountData.count = addToCartCountData.count ? Number(addToCartCountData.count) + 1 : 1;
    addZzSessionData({ name: ADD_TO_CART_COUNT_SESSION_DATA_KEY, value: addToCartCountData });

    if (addToCartCountData.count >= 3) {
      ZigzagEvent.logBraze('add_to_cart_product', {
        add_to_cart_count: addToCartCountData.count,
        add_to_cart_count_store: catalog_product.shop_name,
        shop_id: catalog_product.shop_id,
        catalog_product_id,
      });
    }
  };

  const purchase = () => {
    if (!isZigzagApp) {
      checkoutClickUBL(catalog_product_id);
    }
    const purchasedItems: CreateOrderSheetArgs = {
      order_item_list,
      from: 'product',
      catalog_product_id,
    };

    if (isProductPreview) {
      const dr_log = getRecommendProductDrLogByCatalogProductId(catalog_product_id);
      ZigzagLogEvent.subl({
        category: 'click',
        navigation: ublContext.navigation ? ublContext.navigation : 'zpay_option',
        object_type: 'button',
        object_id: 'checkout',
        object_section: 'bottom_bar',
        data: JSON.stringify({
          ...(funnelType && { funnel_info_type: funnelType }),
          ...(dr_log && {
            server_log: {
              dr_log,
            },
          }),
        }),
      });
    } else {
      ZigzagLogEvent.subl({
        category: 'click',
        navigation: ublContext.navigation ? ublContext.navigation : 'zpay_option',
        object_type: 'button',
        object_id: 'checkout',
        data: JSON.stringify({
          catalog_product_id,
          ...(sendGeneralProductId && { general_product_id: matching_catalog_product_info?.id }),
          ...(sendZigzinProductId && { zonly_product_id: matching_catalog_product_info?.id }),
        }),
      });
    }

    close();
    setTimeout(() => {
      createOrderSheetInPDP(purchasedItems);
    }, 300);
  };

  const editUbl = () => {
    ZigzagLogEvent.subl({
      category: 'click',
      navigation: ublContext.navigation ? ublContext.navigation : 'zpay_option',
      object_type: 'button',
      object_id: 'option_change',
      data: JSON.stringify({
        catalog_product_id,
        ...(sendGeneralProductId && { general_product_id: matching_catalog_product_info?.id }),
        ...(sendZigzinProductId && { zonly_product_id: matching_catalog_product_info?.id }),
      }),
    });
  };

  const addToCart = async (is_bundle_purchase = false) => {
    const dr_log = getRecommendProductDrLogByCatalogProductId(catalog_product_id);
    const product_item_quantity_list = confirmItems.map(
      ({ id, count, additional_option_detail_list, grouping_required_quantity, is_bogo, is_additional }) => ({
        product_item_id: id,
        quantity_delta: count,
        additional_option_detail_list,
        product_id: catalog_product_id,
        ...(funnelType && { funnel_info: { type: funnelType } }),
        ...(is_bogo &&
          !is_additional && {
            grouping_required_quantity,
          }),
      }),
    );

    await addCart({ catalog_product_id, product_item_quantity_list }, () => {
      if (from === 'similar_products' || funnelType === 'cart_list_similar-SIMILAR_IMAGE_PRODUCT') {
        ZpayEvent.reloadCart();
      } else {
        ZpayEvent.addCart();
      }

      if (isProductPreview && (from === 'recommendations' || from === 'cart')) {
        if (is_bundle_purchase) {
          ZigzagLogEvent.subl({
            category: 'add_to_cart',
            navigation: ublContext.navigation ? ublContext.navigation : 'zpay_option',
            navigation_sub: JSON.stringify({ product_id: catalog_product_id }),
            object_type: 'catalog',
            object_id: catalog_product_id,
            object_section: 'bottom_bar',
            ...(funnelType && {
              data: JSON.stringify({
                funnel_info_type: funnelType,
                ...(catalog_product.shop_name && { shop_name: catalog_product.shop_name }),
                ...(catalog_product_id && { catalog_product_id }),
                ...(finalPrice && { price: finalPrice }),
                ...(dr_log && {
                  server_log: {
                    dr_log,
                  },
                }),
              }),
            }),
          });
        } else {
          ZigzagLogEvent.subl({
            category: 'add_to_cart',
            navigation: ublContext.navigation ? ublContext.navigation : 'zpay_option',
            object_section: 'bottom_bar',
            object_type: 'catalog',
            object_id: catalog_product_id,
            ...(funnelType && {
              data: JSON.stringify({
                funnel_info_type: funnelType,
                action_btn: '장바구니추가',
                ...(dr_log && {
                  server_log: {
                    dr_log,
                  },
                }),
              }),
            }),
          });
        }
      } else {
        ZigzagLogEvent.subl({
          category: 'add_to_cart',
          navigation: ublContext.navigation ? ublContext.navigation : 'zpay_option',
          navigation_sub: JSON.stringify({ product_id: catalog_product_id }),
          object_type: 'catalog',
          object_id: catalog_product_id,
          object_section: 'bottom_bar',
          ...((sendGeneralProductId || sendZigzinProductId) && {
            data: JSON.stringify({
              ...(sendGeneralProductId && { general_product_id: matching_catalog_product_info?.id }),
              ...(sendZigzinProductId && { zonly_product_id: matching_catalog_product_info?.id }),
            }),
          }),
        });
      }
      ZigzagLogEvent.logBraze('add_to_cart', {
        shop_name: catalog_product.shop_name,
        shop_domain_name: catalog_product.shop_main_domain,
        shop_id: catalog_product.shop_id,
        product_name: catalog_product.name,
        product_category_lv1: catalog_product.category_list?.[0]?.value,
        price: finalPrice,
        promotion_type: !!first_order_discount?.promotion_id,
        product_category_lv2: catalog_product.managed_category_list
          ?.slice(0, 2)
          .map((o) => o.value)
          .join('/'),
      });

      if (!_isPreview) {
        addToCartEvent({
          managedCategoryList: catalog_product.managed_category_list,
          product_name: catalog_product.name,
          product_id: catalog_product_id,
          price: totalInfo.price,
          items: confirmItems.map((item) => ({
            item_id: catalog_product_id,
            item_name: catalog_product.name,
            price: item.price,
            quantity: item.count,
          })),
          category: catalog_product.category_list?.[0]?.value ?? '',
          shop_id: catalog_product.shop_id + '',
          shop_name: catalog_product.shop_name ?? '',
        });
      }

      if (!isProductPreview) {
        const relatedItemListData = Object.assign(
          {
            scene: zpayRelatedItem.scene,
            action: 'CART',
            productNo: zpayRelatedItem.productNo,
            catalogProductId: zpayRelatedItem.catalogProductId,
            imageUrl: zpayRelatedItem.imageUrl,
          },
          getBottomRelatedButtonData(),
          getAvailableCouponData(),
        );
        ZpayEvent.relatedItemList(relatedItemListData as ZpayRelatedItem);
      }

      if (isProductPreview) {
        Toast.show(is_bundle_purchase ? '상품을 주문에 추가했어요.' : '상품을 장바구니에 담았어요.');
      } else if (!isZigzagApp) {
        Toast.show('장바구니에 상품을 담았습니다.');
        refetchCartCount();
      }
    });

    sendAddToCartBrazeLog();

    if (!isZigzagApp) {
      if (isProductPreview) {
        addToCartUBL(
          catalog_product_id,
          ublContext.navigation,
          { product_id: catalog_product_id },
          {
            ...(catalog_product.shop_name && { shop_name: catalog_product.shop_name }),
            ...(catalog_product_id && { catalog_product_id }),
          },
          funnelType,
          is_bundle_purchase,
          dr_log,
        );
      } else {
        addToCartUBL(catalog_product_id);
      }
    }

    if (initialItem) {
      addItem(initialItem);
    }

    resetAll();
    close();

    const bundle_purchase_item_list = confirmItems.map(({ id, count, price }) => ({
      product_item_id: id,
      quantity: count,
      price,
    }));
    onAddCart?.(is_bundle_purchase ? bundle_purchase_item_list : null);
  };

  return {
    confirmButtons: {
      buttonTypes: {
        from,
        isSample,
        isStoreDeal: Boolean(store_deal_banner),
        isProductPreview,
        showBuyNowOnly: Boolean(is_purchase_only_one_at_time) || Boolean(is_on_site) || !is_cart_button_visible,
        showPurchaseWithKakao,
        showAddToCartInPreview: isGroupOrderBridgeTest,
      },

      actions: {
        purchase,
        purchaseKaKao,
        addToCart,
      },
    },

    _isPreview,
    isMatchingAll,
    isDeliveryTomorrow,
    isZigzinProduct,
    isDigitalShipping: !shipping_fee,
    showAddToCartInPreview: isGroupOrderBridgeTest,

    matching_catalog_product_info,

    discountColor: discount_info_of_atf,
    discountInfo,
    totalInfo,

    finalPrice,
    finalPriceColor: displayFinalPriceAdditional?.color?.normal || displayFinalPriceBadge?.color?.normal || '',
    finalDiscountInfo,
    displayOriginPrice,
    zigzinProductFinalPrice,
    couponDiscountAmount: totalInfo.price - finalPrice,

    confirmItems,
    orderItems,
    zpayRelatedItem,

    shippingFee: catalog_product.shipping_fee,

    sendGeneralProductId,
    sendZigzinProductId,

    hasCouponDiscount,
    hasMatchingZigzin,
    hasMatchingGeneral,
    hasOnlyMatchingZigzin,
    hasZpayDiscount: Boolean(promotionDiscountAmount),

    couponTooltip,

    getBottomRelatedButtonData,
    sendAddToCartBrazeLog,
    editUbl,
    toggleCouponTooltip: () => setCouponTooltip(!couponTooltip),
  };
};
